import * as d3 from 'd3-selection'
import ellipsis from 'ellipsis'

const dimensions = {
  rotatedLabelMaxWidth: 130,
  xAxisTitleHeight: 18,
  xAxisSubtitleHeight: 12,
  gridTopExtraPadding: 7 // extra padding on top when the chart is a rectangular grid (not polar or pie)
}


class CartesianLayout {

  constructor (options) {
    this.previousScaler = options.previousScaler
    this.scaler = options.scalers.x.groupScale
  }

  axisTransform (selection, height) {
    selection.attr('transform', `translate(0, ${height})`)
  }

  enterTransform (selection) {
    let domain0 = this.previousScaler.domain()
      , domain1 = this.scaler.domain()
      , prevStartIdx = domain1.indexOf(domain0[0])
      , prevEndIdx = domain1.indexOf(domain0[domain0.length - 1])
      , step = this.previousScaler.step()
      , prevEndLocation = this.previousScaler(domain0[domain0.length - 1])

    selection.attr('transform', (d, i, all) => {
      let idx = domain1.indexOf(d.id)
        , dx = this.previousScaler.bandwidth() / 2
        , x = this.previousScaler(d.id)

      if (idx >= prevStartIdx && idx <= prevEndIdx) {
        let dx = this.scaler.bandwidth() / 2
          , prevX = this.previousScaler(d.id)
        x = (isFinite(prevX) ? prevX : this.scaler(d.id))
      } else if (idx < prevStartIdx) {
        x = step * (idx - prevStartIdx)
      } else if (prevEndIdx != -1 && idx > prevEndIdx) {
        x = step * (idx - prevEndIdx) + prevEndLocation
      } else if (typeof x === 'undefined') {
        // All new ticks coming, start from left and offset based on the new step
        let j = all.length - 1 - i // reverse the order
        x = -j * this.scaler.step()
      }

      return `translate(${(x || 0) + dx}, 0)`
    })
  }

  exitTransform (selection) {
    let domain0 = this.previousScaler.domain()
      , domain1 = this.scaler.domain()
      , newStartPosition = this.scaler(domain1[0])
      , newEndPosition = this.scaler(domain1[domain1.length - 1])
      , step = this.scaler.step()

    selection.attr('transform', d => {
      let dx = this.scaler.bandwidth() / 2
        , x = this.scaler(d.id)

      if (typeof x === 'undefined') {
        // This tick is no longer part of our domain, so we need to slide it out of view
        let idx = domain0.indexOf(d.id)
          , prevStart = domain0.indexOf(domain1[0])
          , prevEnd = domain0.indexOf(domain1[domain1.length - 1])

        if (idx < prevStart) {
          // Slide this out to the left
          x = newStartPosition - step * (prevStart - idx)
        } else if (idx > prevEnd) {
          // Slide this to the right
          x = newEndPosition + step * (idx - prevEnd)
        }
      }

      return `translate(${(x || 0) + dx}, 0)`
    })
  }

  /*
   * Aligns the ticks in their correct location
   */
  tickTransform (selection, rotate, height) {
    if (!selection.size()) {
      return
    }
    let axis = d3.select(selection.node().parentNode)
      , scaler = this.scaler

    selection.attr('transform', function (d) {
                let dx = scaler.bandwidth() / 2
                  , x = scaler(d.id) + dx

                this._x = x // Store the location in case other x-axis animations needs
                            // this final state
                return `translate(${x}, 0)`
              })
              .select('line')
              .attr('x2', 0)
              .attr('y2', -height)
              .attr('y1', rotate ? 10 : 0)

    /*
     * Most of the time the x-axis tick labels will be horizontal and stacked so the title
     * is above the subtitle. In the situation where we're showing a single period bar chart, we may need
     * to rotate the text labels since we don't want to remove them. See https://github.com/SpiderStrategies/Scoreboard/issues/17165
     */
    let text = selection.selectAll('.text-group')
    if (rotate) {
      axis.classed('axis-rotated-labels', true)
      text.attr('transform', 'rotate(-65 15 5)')
          .select('text')
          .attr('y', 0)
          .attr('dy', 0)
          .select('tspan')
          .call(ellipsis, dimensions.rotatedLabelMaxWidth)
    } else {
      axis.classed('axis-rotated-labels', false)
      text.attr('transform', 'rotate(0)')
          .attr('transform-origin', '')
          .select('text')
          .attr('y', dimensions.xAxisTitleHeight)
          .select('tspan.subtitle')
            .attr('x', 0)
            .attr('y', dimensions.xAxisSubtitleHeight + dimensions.xAxisTitleHeight)
    }
  }

}

export default CartesianLayout

export { dimensions } // TODO probably not ideal
